
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarRange, AlertCircle } from "lucide-react";
import { useCreateTimeSlot } from "@/hooks/useTimeSlots";
import { Service } from "@/hooks/useServices";
import { Room } from "@/hooks/useRooms";
import { format, addDays, eachDayOfInterval } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface BulkAddTimeSlotsDialogProps {
  providerId: string;
  services: Service[];
  rooms: Room[];
  onTimeSlotsAdded?: () => void;
}

interface BulkTimeSlotFormData {
  service_id: string;
  room_id: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  max_bookings: number;
  selected_days: number[]; // 0 = Sunday, 1 = Monday, etc.
}

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

const BulkAddTimeSlotsDialog = ({ providerId, services, rooms, onTimeSlotsAdded }: BulkAddTimeSlotsDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const createTimeSlot = useCreateTimeSlot();
  const { toast } = useToast();
  
  const form = useForm<BulkTimeSlotFormData>({
    defaultValues: {
      max_bookings: 1,
      selected_days: [],
    },
  });

  const selectedServiceId = form.watch("service_id");
  const selectedService = services.find(s => s.id === selectedServiceId);

  const handleDayToggle = (dayValue: number, checked: boolean) => {
    if (checked) {
      setSelectedDays(prev => [...prev, dayValue]);
    } else {
      setSelectedDays(prev => prev.filter(day => day !== dayValue));
    }
  };

  const calculateSlots = (startTime: string, endTime: string, serviceDuration: number, reservedDuration: number) => {
    const start = new Date(`2024-01-01T${startTime}`);
    const end = new Date(`2024-01-01T${endTime}`);
    
    const totalMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
    const totalSlotDuration = serviceDuration + (reservedDuration || 0);
    
    return Math.floor(totalMinutes / totalSlotDuration);
  };

  const previewSlots = () => {
    const formData = form.getValues();
    if (!selectedService || !formData.start_time || !formData.end_time || selectedDays.length === 0) return null;

    const slotsPerDay = calculateSlots(
      formData.start_time, 
      formData.end_time, 
      selectedService.duration_minutes,
      selectedService.reserved_duration_minutes || 0
    );

    const startDate = new Date(formData.start_date);
    const endDate = new Date(formData.end_date);
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Filter days based on selected days of week
    const filteredDays = allDays.filter(day => selectedDays.includes(day.getDay()));

    return {
      slotsPerDay,
      totalDays: filteredDays.length,
      totalSlots: slotsPerDay * filteredDays.length,
      totalSlotDuration: selectedService.duration_minutes + (selectedService.reserved_duration_minutes || 0),
      serviceDuration: selectedService.duration_minutes,
      reservedDuration: selectedService.reserved_duration_minutes || 0,
      selectedDaysCount: selectedDays.length,
      selectedDaysNames: selectedDays.map(dayNum => 
        DAYS_OF_WEEK.find(d => d.value === dayNum)?.label
      ).join(", ")
    };
  };

  const onSubmit = async (data: BulkTimeSlotFormData) => {
    if (!selectedService || selectedDays.length === 0) return;

    setIsCreating(true);
    setErrors([]);
    
    let successCount = 0;
    let skippedCount = 0;
    let failureCount = 0;
    const failedSlots: string[] = [];
    
    try {
      const startDate = new Date(data.start_date);
      const endDate = new Date(data.end_date);
      const allDays = eachDayOfInterval({ start: startDate, end: endDate });
      
      // Filter days based on selected days of week
      const filteredDays = allDays.filter(day => selectedDays.includes(day.getDay()));

      const totalSlotDuration = selectedService.duration_minutes + (selectedService.reserved_duration_minutes || 0);
      
      console.log('🚀 BULK ADD: Starting creation process', {
        totalDays: filteredDays.length,
        serviceDuration: selectedService.duration_minutes,
        reservedDuration: selectedService.reserved_duration_minutes,
        totalSlotDuration,
        providerId,
        serviceId: data.service_id,
        roomId: data.room_id,
      });
      
      for (const day of filteredDays) {
        const startTime = new Date(`${format(day, 'yyyy-MM-dd')}T${data.start_time}`);
        const endTime = new Date(`${format(day, 'yyyy-MM-dd')}T${data.end_time}`);
        
        let currentSlotStart = new Date(startTime);
        
        while (currentSlotStart < endTime) {
          const currentSlotEnd = new Date(currentSlotStart.getTime() + totalSlotDuration * 60000);
          
          if (currentSlotEnd <= endTime) {
            const slotData = {
              service_id: data.service_id,
              provider_id: providerId,
              room_id: data.room_id,
              start_time: currentSlotStart.toISOString(),
              end_time: currentSlotEnd.toISOString(),
              max_bookings: data.max_bookings,
              current_bookings: 0,
              is_available: true,
              womens_only: false,
            };
            
            try {
              console.log('📝 Creating slot:', {
                date: format(day, 'yyyy-MM-dd'),
                start: format(currentSlotStart, 'HH:mm'),
                end: format(currentSlotEnd, 'HH:mm'),
              });
              
              await createTimeSlot.mutateAsync(slotData);
              successCount++;
            } catch (slotError: any) {
              if (slotError?.code === '23505') {
                skippedCount++;
                console.log('⏭️ Skipped duplicate slot:', format(day, 'MMM dd'), format(currentSlotStart, 'HH:mm'));
              } else {
                failureCount++;
                const errorMsg = `${format(day, 'MMM dd')} ${format(currentSlotStart, 'HH:mm')}-${format(currentSlotEnd, 'HH:mm')}: ${slotError?.message || 'Unknown error'}`;
                failedSlots.push(errorMsg);
                console.error('❌ Failed to create slot:', {
                  slot: slotData,
                  error: slotError,
                });
              }
            }
          }
          
          currentSlotStart = new Date(currentSlotEnd);
        }
      }

      console.log('✅ BULK ADD COMPLETE:', {
        successCount,
        failureCount,
        totalAttempted: successCount + failureCount,
      });

      // Show results
      const skippedMsg = skippedCount > 0 ? `, skipped ${skippedCount} existing` : '';
      if (failureCount === 0) {
        toast({
          title: "Success!",
          description: `Created ${successCount} time slot${successCount !== 1 ? 's' : ''}${skippedMsg}.`,
        });
        form.reset();
        setSelectedDays([]);
        setErrors([]);
        setOpen(false);
        onTimeSlotsAdded?.();
      } else if (successCount > 0) {
        toast({
          title: "Partially Complete",
          description: `Created ${successCount} slot${successCount !== 1 ? 's' : ''}, but ${failureCount} failed.`,
          variant: "destructive",
        });
        setErrors(failedSlots);
      } else {
        toast({
          title: "All Slots Failed",
          description: `Failed to create any slots. Check the errors below.`,
          variant: "destructive",
        });
        setErrors(failedSlots);
      }
    } catch (error: any) {
      console.error('💥 BULK ADD ERROR:', error);
      toast({
        title: "Error",
        description: error?.message || "An unexpected error occurred",
        variant: "destructive",
      });
      setErrors([error?.message || "An unexpected error occurred"]);
    } finally {
      setIsCreating(false);
    }
  };

  const preview = previewSlots();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <CalendarRange className="h-4 w-4 mr-2" />
          Bulk Add Slots
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Add Time Slots</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="service_id"
              rules={{ required: "Please select a service" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} ({service.duration_minutes}min
                          {service.reserved_duration_minutes ? ` + ${service.reserved_duration_minutes}min reserved` : ''})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="room_id"
              rules={{ required: "Please select a room" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Room</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a room" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {rooms.map((room) => (
                        <SelectItem key={room.id} value={room.id}>
                          {room.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_date"
                rules={{ required: "Please select start date" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_date"
                rules={{ required: "Please select end date" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Days of Week Selection */}
            <div className="space-y-3">
              <FormLabel>Select Days of Week</FormLabel>
              <div className="grid grid-cols-2 gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div key={day.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`day-${day.value}`}
                      checked={selectedDays.includes(day.value)}
                      onCheckedChange={(checked) => handleDayToggle(day.value, checked as boolean)}
                    />
                    <label htmlFor={`day-${day.value}`} className="text-sm font-medium">
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>
              {selectedDays.length === 0 && (
                <p className="text-sm text-red-600">Please select at least one day of the week</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="start_time"
                rules={{ required: "Please select start time" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_time"
                rules={{ required: "Please select end time" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End Time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="max_bookings"
              rules={{ 
                required: "Please enter max bookings",
                min: { value: 1, message: "Must be at least 1" }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Bookings per Slot</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min="1" 
                      {...field} 
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Some Slots Failed to Create</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 max-h-40 overflow-y-auto text-xs">
                    {errors.map((error, index) => (
                      <div key={index} className="mb-1">• {error}</div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {preview && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Preview</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Selected days:</strong> {preview.selectedDaysNames}</p>
                  <p><strong>Slots per day:</strong> {preview.slotsPerDay}</p>
                  <p><strong>Total matching days:</strong> {preview.totalDays}</p>
                  <p><strong>Total slots to create:</strong> {preview.totalSlots}</p>
                  <p><strong>Session duration:</strong> {preview.serviceDuration} minutes</p>
                  <p><strong>Reserved time:</strong> {preview.reservedDuration} minutes</p>
                  <p><strong>Total slot duration:</strong> {preview.totalSlotDuration} minutes each</p>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isCreating || !preview?.totalSlots || selectedDays.length === 0}
              >
                {isCreating ? "Creating..." : `Create ${preview?.totalSlots || 0} Slots`}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BulkAddTimeSlotsDialog;